import { useEffect } from 'react'
import { Flex, Form } from 'antd'
import { FormattedMessage } from 'react-intl'
import { MediaPricesLimits } from '../hooks/useMediaPricesLimits'
import Image from './Image'
import Video from './Video'
import { lang } from './lang'
import { formKeys } from '@/pages/Donation/components/DonationForm/constants'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import Label from '@/shared/components/Label'
import { CustomizeType, FormValues } from '@/pages/Donation/types'

type Props = {
  mediaPricesLimits: MediaPricesLimits
  onSetFiatAmount: (value: string) => void
}

export default function Media({ mediaPricesLimits, onSetFiatAmount }: Props) {
  const form = Form.useFormInstance<FormValues>()
  const customizeType = Form.useWatch<FormValues['customizeType']>(formKeys.customizeType)
  const selectedImage = Form.useWatch<FormValues['image']>(formKeys.image)
  const baseCurrency = Form.useWatch<FormValues['baseCurrency']>(formKeys.baseCurrency)
  const { config } = useStreamerConfigContext()
  const isImageEnabled = config?.image_donation_config?.enabled && config?.image_donation_config?.assets?.length > 0
  const isVideoEnabled = config?.video_donation_config?.enabled
  const defaultImage = config?.image_donation_config?.assets?.find(asset => asset.is_default)?.id ?? null

  const handleSetSelectedImage = (name: string | null) => form.setFieldValue(formKeys.image, name)

  const handleChangeCustomizeType = (type: CustomizeType[number]) => {
    form.setFieldValue(formKeys.customizeType, customizeType?.concat([type]) || [])

    if (type === 'image' && !selectedImage) {
      handleSetSelectedImage(defaultImage)
    }
  }

  const handleClose = (type: CustomizeType[number]) => {
    form.setFieldValue(formKeys.customizeType, customizeType?.filter(currentType => currentType !== type) || [])

    if (type === 'image') {
      handleSetSelectedImage(null)
    }

    if (type === 'video') {
      form.setFieldValue(formKeys.youtubeLink, '')
      form.setFieldValue(formKeys.youtubeStart, '')
    }
  }

  const handleAddToAmount = (type: CustomizeType[number]) => {
    const prices = mediaPricesLimits[type]

    if (!prices) {
      return
    }

    handleChangeCustomizeType(type)
    onSetFiatAmount(String(prices.totalMinAmount))
  }

  const handleClearCustomizeType = (type: CustomizeType[0]) => {
    form.setFieldValue(formKeys.customizeType, customizeType?.filter(currentType => currentType !== type) || [])
    if (type === 'image') {
      handleSetSelectedImage(null)
    }

    if (type === 'video') {
      form.setFieldValue(formKeys.youtubeLink, '')
      form.setFieldValue(formKeys.youtubeStart, '')
    }
  }

  useEffect(() => {
    if (mediaPricesLimits.image && customizeType?.includes('image')) {
      handleClearCustomizeType('image')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaPricesLimits.image, customizeType])

  useEffect(() => {
    if (mediaPricesLimits.video && customizeType?.includes('video')) {
      handleClearCustomizeType('video')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaPricesLimits.video, customizeType])

  return (
    <Flex vertical gap={4}>
      <Label>
        <FormattedMessage {...lang.customizeTitle} />
      </Label>
      <Form.Item name={formKeys.customizeType} noStyle />
      <Flex vertical gap={12}>
        {isImageEnabled ? (
          <Form.Item name={formKeys.image} noStyle>
            <Image
              baseCurrency={baseCurrency}
              mediaPriceLimit={mediaPricesLimits.image}
              isSelected={!!customizeType?.includes('image')}
              selectedImageId={selectedImage}
              onSelectImage={handleSetSelectedImage}
              onChangeCustomizeType={handleChangeCustomizeType}
              onAddAmount={handleAddToAmount}
              handleClose={handleClose}
            />
          </Form.Item>
        ) : null}
        {isVideoEnabled ? (
          <Video
            baseCurrency={baseCurrency}
            mediaPriceLimit={mediaPricesLimits.video}
            isSelected={!!customizeType?.includes('video')}
            onChangeCustomizeType={handleChangeCustomizeType}
            onAddAmount={handleAddToAmount}
            handleClose={handleClose}
            form={form}
          />
        ) : null}
      </Flex>
    </Flex>
  )
}
